import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { Close } from '@mui/icons-material';
import { Box, Button, Card, Chip, IconButton, Stack, Typography } from '@mui/material';

import MuiDialog from 'common/components/MuiDialog';
import MuiLoading from 'common/components/MuiLoading';
import useService from 'common/hook/useService';
import SampleMealGallery from 'modules/map/components/daily-dish/sample-meal/SampleMealGallery';
import { filterSampleMealDays } from 'modules/map/components/daily-dish/sample-meal/eatingDays';
import { coverPhoto } from 'modules/map/components/daily-dish/sample-meal/photoSort';
import type { SampleMealDay } from 'modules/map/components/daily-dish/sample-meal/types';

interface Props {
  open: boolean;
  onClose: () => void;
  siteCode: string;
  weekStart: string | null;
  /** Ngày ăn đầu/cuối trên thực đơn rút gọn của tuần đang xem. */
  fromDate?: string | null;
  toDate?: string | null;
}

const SampleMealPhotosDialog = (props: Props) => {
  const { open, onClose, siteCode, weekStart, fromDate, toDate } = props;
  const service = useService();
  const [galleryDay, setGalleryDay] = useState<SampleMealDay | null>(null);

  const { data, isFetching, isError, refetch } = useQuery({
    queryKey: [siteCode, weekStart, 'sample-meals'],
    queryFn: async () => {
      if (!service || !siteCode || !weekStart) return null;
      return service.LogiController_listSampleMeals({
        queries: { site_code: siteCode, week_start: weekStart },
      });
    },
    enabled: open && !!service && !!siteCode && !!weekStart,
  });

  const days = useMemo(
    () => filterSampleMealDays(data?.days ?? [], { from: fromDate, to: toDate }),
    [data?.days, fromDate, toDate]
  );

  const renderContent = () => {
    if (!weekStart) {
      return (
        <Typography textAlign="center" color="text.secondary" p={2}>
          <FormattedMessage id="map.noWeek" />
        </Typography>
      );
    }
    if (isFetching) return <MuiLoading />;
    if (isError) {
      return (
        <Stack spacing={2} alignItems="center" p={3}>
          <Typography color="error">
            <FormattedMessage id="map.sampleMealPhotosLoadError" />
          </Typography>
          <Button variant="outlined" onClick={() => refetch()}>
            <FormattedMessage id="map.sampleMealPhotosRetry" />
          </Button>
        </Stack>
      );
    }

    return (
      <Box
        sx={{
          px: 3,
          pb: 3,
          display: 'grid',
          gap: 2,
          gridTemplateColumns: {
            xs: 'repeat(1, minmax(0, 1fr))',
            tablet: 'repeat(2, minmax(0, 1fr))',
            desktop: 'repeat(3, minmax(0, 1fr))',
          },
        }}
      >
        {days.map((day) => {
          const photos = day.photos ?? [];
          const count = day.count ?? photos.length;
          const cover = coverPhoto(photos);
          const dateLabel = day.photo_date ? dayjs(day.photo_date).format('DD/MM/YYYY') : '—';

          return (
            <Card
              key={day.photo_date ?? dateLabel}
              variant="outlined"
              onClick={() => {
                if (count > 0) setGalleryDay(day);
              }}
              sx={{
                p: 1.5,
                cursor: count > 0 ? 'pointer' : 'default',
                borderRadius: 2,
                transition: 'all 0.2s ease',
                '&:hover': count > 0 ? { boxShadow: 3, borderColor: 'primary.main' } : undefined,
              }}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography fontWeight={700}>{dateLabel}</Typography>
                <Chip
                  size="small"
                  color={count > 0 ? 'primary' : 'default'}
                  label={`${count} ảnh`}
                />
              </Stack>

              {count === 0 || !cover?.url ? (
                <Box
                  sx={{
                    height: 140,
                    borderRadius: 1,
                    border: '1px dashed',
                    borderColor: 'divider',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'text.secondary',
                    bgcolor: 'grey.50',
                  }}
                >
                  <Typography variant="body2">
                    <FormattedMessage id="map.sampleMealPhotosEmptyDay" />
                  </Typography>
                </Box>
              ) : (
                <Box
                  component="img"
                  src={cover.url}
                  alt={dateLabel}
                  sx={{
                    width: '100%',
                    height: 140,
                    objectFit: 'cover',
                    borderRadius: 1,
                  }}
                />
              )}
            </Card>
          );
        })}
      </Box>
    );
  };

  return (
    <>
      <MuiDialog
        open={open}
        onClose={onClose}
        maxWidth="desktop"
        fullWidth
        disablePortal
        disableCloseBtn
        titleAlign="center"
        PaperProps={{
          sx: {
            margin: 2,
            height: '100%',
            maxWidth: 'lg',
          },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Typography sx={{ textAlign: 'right', m: 2 }}>
            <IconButton onClick={onClose} size="small" color="primary">
              <Close fontSize="small" />
            </IconButton>
          </Typography>
          <Typography variant="h5" sx={{ textAlign: 'center', color: 'primary.main', mb: 2 }}>
            <FormattedMessage id="map.sampleMealPhotos" />
          </Typography>
          <Box sx={{ flex: 1, overflow: 'auto' }}>{renderContent()}</Box>
        </Box>
      </MuiDialog>

      <SampleMealGallery
        open={!!galleryDay}
        onClose={() => setGalleryDay(null)}
        photos={galleryDay?.photos ?? []}
        title={
          galleryDay?.photo_date
            ? dayjs(galleryDay.photo_date).format('DD/MM/YYYY')
            : undefined
        }
      />
    </>
  );
};

export default SampleMealPhotosDialog;
